import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import DialogContent from '@material-ui/core/DialogContent';
import LoadingOverlay from '@configuratorware/configurator-frontendgui/Framework/Components/LoadingOverlay';
import CustomDialog from '@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import Option from '../Option';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useSelector } from 'react-redux';
import GroupPriceComponent from '../GroupPriceComponent';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { isEmptyOptionSelected } from '../../Reducers/Configurator/Selectors';
import MainButton from '@configuratorware/configurator-frontendgui/Shared/Components/MainButton';

const useStyles = makeStyles(
  (theme) => {
    return {
      dialogStyle: {
        height: '100%',
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          maxWidth: '390px',
        },
        [theme.breakpoints.between('sm', 'xl')]: {
          width: '470px',
          maxWidth: '470px',
        },
        [theme.breakpoints.up('md')]: {
          marginLeft: '120px',
          '& .MuiBackdrop-root': {
            left: '140px',
          },
        },
      },
      [theme.breakpoints.between('sm', 'md')]: {
        [theme.orientation.portrait]: {
          top: '-110px !important',
        },
      },
      '& > div.MuiDialog-container': {
        [theme.breakpoints.between('sm', 'lg')]: {
          [theme.orientation.portrait]: {
            alignItems: 'flex-end',
          },
          [theme.orientation.landscape]: {
            alignItems: 'flex-start',
            marginTop: 35,
          },
        },
      },
      contentStyle: {
        paddingTop: 0,
      },
      backDrop: {
        backgroundColor: 'rgba(0,0,0,0.0)',
      },
      accordionOptionList: {
        flex: 1,
      },
      accordion: {
        boxShadow: 'none',
        margin: '0 !important',
        '&::before': {
          display: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: 'initial',
        },
      },
      accordionSummary: {
        backgroundColor: '#fff',
        //display: 'inline-flex',
        paddingLeft: 0,
        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
          transform: 'rotate(90deg)',
        },
        '& .MuiAccordionSummary-content': {
          marginLeft: 0,
        },
        '& .MuiIconButton-root': {
          color: 'inherit',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          marginBottom: 0,
        },
        '&.Mui-disabled.Mui-expanded': {
          opacity: 1,
        },
      },
      accordionDetails: {
        padding: theme.spacing(2),
      },
      accordionHeading: {
        fontWeight: 'bold',
      },
      closeButton: {
        backgroundColor: 'black',
        display: 'flex',
        height: '50px',
        marginTop: '10px',
        marginBottom: '10px',
        [theme.breakpoints.down('xs')]: {
          height: '40px',
        },
      },
      closeButtonLabel: {
        [theme.breakpoints.down('xs')]: {
          whiteSpace: 'normal',
          fontSize: 12,
        },
        whiteSpace: 'nowrap',
      },
      closeIcon: {
        display: 'none',
      },
    };
  },
  {
    name: 'OptionsList',
  }
);

const OptionsList = ({ render, ...props }) => {
  const {
    addCheckedOptionclassification,
    children,
    isLoading,
    onClose,
    onSelect,
    onToggle,
    optionclassification,
    options,
    show,
    viewDetails,
    viewStockInformation,
    LoadingComponent,
    OptionComponent,
    isAdminMode,
    setOptionInputText,
    componentGroupComponents,
  } = props;

  const classes = useStyles();

  const firstComponentIdentifier = componentGroupComponents?.[0]?.identifier;

  const emptyOptionSelected = isEmptyOptionSelected(componentGroupComponents?.[0]);

  const [expanded, setExpanded] = useState(firstComponentIdentifier || 'table_stand');

  useEffect(() => {
    setExpanded(firstComponentIdentifier);
  }, [firstComponentIdentifier]);

  const optionclassifications = useSelector(
    (state) => state.configurator.configuration.optionclassifications
  );

  const createOptions = (options, component, isTable) => {
    const onOptionClicked = () => {
      addCheckedOptionclassification(component);
    };

    return options.map((option) => {
      let optionIndex = -1;

      const optionGroupChildren = get(option, 'option_group.children', []);

      if (optionGroupChildren.length === 0) {
        if (componentGroupComponents) {
          let optionclassification;
          optionclassifications.forEach((element) => {
            if (element.identifier === component.identifier) {
              optionclassification = element;
            }
          });
          optionIndex = findIndex(get(optionclassification, 'selectedoptions'), {
            identifier: option.identifier,
          });
        } else {
          optionIndex = findIndex(get(component, 'selectedoptions'), {
            identifier: option.identifier,
          });
        }
      } else {
        for (const item of optionGroupChildren) {
          optionIndex = findIndex(get(component, 'selectedoptions'), {
            identifier: item.identifier,
          });
          if (optionIndex > -1) {
            // we need the first selected option
            break;
          }
        }
      }

      const selected = optionIndex <= -1 ? false : !(option.amount_is_selectable && option.amount < 1);

      return (
        <OptionComponent
          key={'optionlistitem' + option.identifier}
          option={option}
          optionclassification={component}
          onSelect={onSelect}
          selected={selected}
          viewDetails={viewDetails}
          viewStockInformation={viewStockInformation}
          onClick={onOptionClicked}
          onInputTextChange={setOptionInputText}
          onToggle={onToggle}
          isAdminMode={isAdminMode}
          isTable={isTable}
        />
      );
    });
  };

  const renderOptions = (options) => {
    const isTable = false;
    const optionListItems = createOptions(options, optionclassification, isTable);
    return (
      <div>
        {children ? children : optionListItems.length > 0 ? optionListItems : null}
        <LoadingComponent show={isLoading} />
      </div>
    );
  };

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const renderComponents = (components) => {
    const isTable = true;
    return components.map((component) => (
      <Accordion
        key={component.identifier}
        expanded={expanded === component.identifier}
        onChange={handleChange(component.identifier)}
        className={clsx(classes.accordion)}
        disabled={emptyOptionSelected}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={clsx(classes.accordionSummary)}
        >
          <Typography className={clsx(classes.accordionHeading)}>{component.title}</Typography>
        </AccordionSummary>
        <AccordionDetails className={clsx(classes.accordionDetails)}>
          <div className={classes.accordionOptionList}>
            {createOptions(component.selectableOptions, component, isTable)}
          </div>
        </AccordionDetails>
      </Accordion>
    ));
  };

  const renderOptionsList = () => {
    return (
      <CustomDialog
        className={clsx(classes.dialogStyle, customClassName('options'))}
        dialogTitle={optionclassification.title && t(optionclassification.title)}
        onCancel={onClose}
        onClose={onClose}
        open={show && !!optionclassification.identifier}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
        classes={{ closeIcon: classes.closeIcon }}
      >
        <DialogContent className={classes.contentStyle}>
          {componentGroupComponents ? renderComponents(componentGroupComponents) : renderOptions(options)}
          {componentGroupComponents && !emptyOptionSelected && (
            <GroupPriceComponent optionclassification={optionclassification} />
          )}
          <MainButton
            onClick={onClose}
            className={customClassName('close-button')}
            variant="contained"
            fullWidth
            size="medium"
            classes={{
              root: classes.closeButton,
              label: classes.closeButtonLabel,
            }}
          >
            {t('closeButton')}
          </MainButton>
        </DialogContent>
      </CustomDialog>
    );
  };

  const getRenderProps = () => ({
    ...props,
    renderOptions,
    createOptions,
    renderOptionsList,
  });

  return typeof render === 'function' ? render(getRenderProps()) : renderOptionsList();
};

OptionsList.defaultProps = {
  addCheckedOptionclassification: () => {},
  isLoading: false,
  onClose: () => {},
  onSelect: () => {},
  onToggle: () => {},
  options: [],
  optionclassification: {},
  show: false,
  viewDetails: () => {},
  viewStockInformation: () => {},
  LoadingComponent: LoadingOverlay,
  OptionComponent: Option,
};

OptionsList.propTypes = {
  addCheckedOptionclassification: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  setOptionInputText: PropTypes.func,
  options: PropTypes.array,
  isPavillon: PropTypes.bool,
  optionclassification: PropTypes.object,
  show: PropTypes.bool,
  viewDetails: PropTypes.func,
  viewStockInformation: PropTypes.func,
  LoadingComponent: PropTypes.elementType,
  OptionComponent: PropTypes.elementType,
  isAdminMode: PropTypes.bool,
  render: PropTypes.func,
};

export default OptionsList;
